import React, { ReactNode } from "react";
import { ChangeEvent } from "react";
import { Prompt, RouteComponentProps } from "react-router-dom";

import { V1, V2 } from "../../Api";
import { formatPhone } from "../../Utilities";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import { IAuthProps } from "../AuthContext";
import FileViewDialog from "../FileViewDialog";

import "../../spinner.css";
import "../../table.css";
import "../viewer.css";

interface IFairLocationMergeViewState {
    error?: string;
    isSubmitting?: string | boolean;
    isLoading: boolean;
    isViewerOpen: boolean;
    hasChanged: boolean;

    viewerFileId: string;

    leftCompany: any;
    rightCompany: any;
}


class FairLocationMergeView extends React.Component<RouteComponentProps<any> & IAuthProps, IFairLocationMergeViewState> {
    private totalToCount = 11;
    constructor(props: Readonly<RouteComponentProps<any> & IAuthProps>) {
        super(props);

        this.state = {
            isLoading: true,
            isViewerOpen: false,
            hasChanged: false,

            viewerFileId: "",

            leftCompany: {},
            rightCompany: {}
        };

        this.mergeCompanies = this.mergeCompanies.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.mergePropertySelected = this.mergePropertySelected.bind(this);
        this.closeViewer = this.closeViewer.bind(this);
    }

    public async componentDidMount() {
        try {
            const [left, right] = await Promise.all([
                V1.get(`fairlocations/${this.props.match.params.left}`),
                V1.get(`fairlocations/${this.props.match.params.right}`),
            ]);

            this.setState({
                isLoading: false,
                leftCompany: left,
                rightCompany: right
            });
        }
        catch (e) {
            this.setState({
                error: e.message,
                isLoading: false
            });
        }
    }

    public render() {
        if (this.state.isLoading) {
            return (
                <div className="viewer-root">
                    <div className="spinner-center lds-dual-ring-big" />
                </div>
            );
        }

        if (this.state.error) {
            return (
                <div className="viewer-root">
                    <span className="error-text">{this.state.error}</span>
                </div>
            );
        }

        const { leftCompany: left, rightCompany: right } = this.state;

        this.totalToCount = 11;

        return (
            <div className="viewer-root with-actionbar input-form">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <FileViewDialog isOpen={this.state.isViewerOpen} fileId={this.state.viewerFileId || ""} onRequestClose={this.closeViewer} />
                <p>
                    Select the properties below that you want to keep in the merged company.
                </p>
                <table className="table-vertical table-choice">
                    <thead>
                        <tr>
                            <th>Property</th>
                            <th>First</th>
                            <th>Second</th>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr><th>First vs Second</th></tr>
                    </thead>
                    <tbody>
                        {this.renderItemRow(left.name, right.name, "Name", "name")}
                        {this.renderItemRow(left.address.street, right.address.street, "Address line 1", "address.street")}
                        {this.renderItemRow(left.address.city, right.address.city, "City", "address.city")}
                        {this.renderItemRow(left.address.state, right.address.state, "State", "address.state")}
                        {this.renderItemRow(left.address.zip, right.address.zip, "ZIP Code", "address.zip")}
                        {this.renderItemRow(left.status, right.status, "Status", "status", this.statusToText)}
                        <tr>
                            <td colSpan={10} className="spacer" />
                        </tr>
                        {this.renderItemRow(left.employeeCount, right.employeeCount, "Employees", "employeeCount")}
                        {this.renderItemRow(left.insurance, right.insurance, "Insurance", "insurance")}
                        {this.renderItemRow(left.insuranceFileId, right.insuranceFileId, "Proof of Insurance", "insuranceFileId", v => <>
                            {v ? <span>Has proof <button className="link" onClick={() => this.viewerLink(v)}>View</button></span> : <span className="text-light">None uploaded</span>}
                        </>)}
                        <tr>
                            <td colSpan={10} className="spacer" />
                        </tr>
                        {this.renderItemRow(left.contact.name, right.contact.name, "Contact Name", "contact.name")}
                        {this.renderItemRow(left.contact.phone, right.contact.phone, "Contact Phone", "contact.phone", formatPhone)}
                        {this.renderItemRow(left.contact.email, right.contact.email, "Contact Email", "contact.email")}
                        <tr>
                            <td colSpan={10} className="spacer" />
                        </tr>
                        <tr>
                            <td>Company Notes</td>
                            <td colSpan={2}>Will have both</td>
                        </tr>
                        <tr>
                            <td>Health Fairs</td>
                            <td colSpan={2}>Will have both</td>
                        </tr>
                    </tbody>
                </table>
                &nbsp;
                {typeof this.state.isSubmitting === "string"
                    ? <span className="error-text">{this.state.isSubmitting}</span>
                    : this.state.isSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : null}
                <div className="action-panel">
                    <button className="button blue btn-confirm push-right" disabled={this.state.isSubmitting === true} onClick={this.mergeCompanies}>Merge</button>
                    <button className="button blue btn-cancel" disabled={this.state.isSubmitting === true} onClick={this.cancelChanges}>Cancel</button>
                </div>
            </div>
        );
    }

    private mergePropertySelected(e: ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            hasChanged: true,
            ["select_" + name]: value
        } as any);
    }

    private async mergeCompanies() {
        try {
            this.setState({
                isSubmitting: true
            });

            const keys = Object.keys(this.state).filter(a => a.startsWith("select_"));
            const resultObject = {};

            // Select proper values
            for (const key of keys) {
                const side = (this.state as any)[key];
                let sourceObject = side === "left"
                    ? this.state.leftCompany
                    : this.state.rightCompany;
                let targetObject = resultObject;
                let keyProp = key.substring(key.indexOf("_") + 1);

                if (keyProp.includes(".")) {
                    const path = keyProp.split('.');
                    for (let i = 0; i < path.length - 1; i++) {
                        sourceObject = (sourceObject as any)[path[i]] || {};
                        if ((targetObject as any)[path[i]] === undefined) {
                            (targetObject as any)[path[i]] = {};
                        }
                        targetObject = (targetObject as any)[path[i]];
                    }
                    keyProp = path[path.length - 1];
                }

                (targetObject as any)[keyProp] = (sourceObject as any)[keyProp];
            }

            if (keys.length < this.totalToCount) {
                throw new Error("You must select a value for each property"); // Not all filled out
            }

            await V2.post(`fairlocations/merge/${this.props.match.params.left}/${this.props.match.params.right}`, resultObject);

            this.setState({
                hasChanged: false,
                isSubmitting: false
            }, () => this.props.history.go(-2));
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message
            });
        }
    }

    private cancelChanges() {
        this.props.history.goBack();
    }

    private closeViewer() {
        this.setState({
            isViewerOpen: false
        });
    }

    private viewerLink(fileId: string) {
        this.setState({
            viewerFileId: fileId,
            isViewerOpen: true
        });
    }

    private renderItemRow(left: any, right: any, displayText: string, name: string, valueFormatter?: (value: any) => ReactNode) {
        const formattedLeft = valueFormatter ? valueFormatter(left) : left;
        const formattedRight = valueFormatter ? valueFormatter(right) : right;
        if (left === right) {
            this.totalToCount--;
        }

        return <tr>
            <td>{displayText}</td>
            <td><label><input type="radio" name={name} value="left" onChange={this.mergePropertySelected} disabled={left === right} defaultChecked={left === right} /> {(formattedLeft || <span className="text-light">(blank)</span>)}</label></td>
            <td>
                {left !== right
                    ? <label><input type="radio" name={name} value="right" onChange={this.mergePropertySelected} /> {(formattedRight || <span className="text-light">(blank)</span>)}</label>
                    : <span className="text-light">(same)</span>
                }
            </td>
        </tr>;
    }

    private statusToText(status: number) {
        switch (status) {
            case 2: return "Dot not call";
            case 1: return "OK to call";
            default: return (status || "").toString();
        }
    }
}

export default withTitle("Merge Companies", withAuth(FairLocationMergeView));
